import React from 'react';
import { Container, Title, Form, ButtonContent } from './styles';
import {
  httpRequestAccount,
  fetchAccount,
  resetAccountState,
} from '../../../store/account';
import {
  createAccount,
  editAccount,
  getUserById,
} from '../../../utils/httpRequest';
import { Button, FormController } from '../../../assets/global/globalStyles';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { userForm } from '../../../utils/formVerifications';
import { useDispatch, useSelector } from 'react-redux';
import MultipleSelect from '../MultipleSelect';
import Permission from '../../Permission';
import MetaTitle from '../../MetaTitle';
import Textarea from '../Textarea';
import Select from '../Select';
import Input from '../Input';
import USER_GROUPS, { ALL_WAREHOUSE_PERMISSION } from '../../../global/variables/userGroups';
import USER_ROLES from '../../../global/variables/userRoles';
import { fetchWarehouses } from '../../../store/warehouses';

export default function FormUser({ openModal }) {
  const { id } = useParams();
  const {
    user,
    warehouses: { data: warehouseData },
  } = useSelector((state) => state);

  const accountState = useSelector(state => state.account);
  const {loading: loadingAccount, data: account} = useSelector(state => state.account)

  const title = id ? 'Editar' : 'Cadastrar';
  const [selectedUserGroup, setSelectedUserGroup] = React.useState(USER_GROUPS.DHL)
  const [selectedUserPermissions, setSelectedUserPermissions] = React.useState();
  const formRef = React.useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  React.useEffect(() => {
    if(id && !account?.id && !user.loading){
      dispatch(fetchAccount(getUserById(id)))?.then(response=>{
        if(!response){
          navigate(-1)
        }
      })
    }

    return () => {
      dispatch(resetAccountState());
    }
  }, [dispatch, id, user]);

  React.useEffect(()=>{
    if(account){
      const userGroupsKeys = Object.keys(USER_GROUPS);
      const validGroups = account.userGroup?.filter(group => userGroupsKeys.includes(group))
      
      if(validGroups?.length > 0){
        setSelectedUserGroup(validGroups[0]);
      }
    }
  }, [account])

  React.useEffect(()=>{
    if(selectedUserGroup !== USER_GROUPS.DHL){
      const requestGroup = selectedUserGroup.split('_')[0];
      
      dispatch(
        fetchWarehouses({
          url: `/warehouse/warehouses-by-group/${requestGroup}`,
          method: 'GET',
        })
      );
    }
  },[selectedUserGroup])

  const getUserClaims = (array) => {
    if(array?.length > 0)
      return array.filter(value => warehouseData.find(warehouse => warehouse?.warehouseCode === value))
    else
      return []
  }  

  const handleSubmit = async (data) => {
    formRef.current.setErrors({});
    const { payload, error } = await userForm(data, id, selectedUserGroup);

    if (error) return formRef.current.setErrors(error);
    if (payload) {
      const userClaims = getUserClaims(payload.userLinks);
      
      const user = {
        userName: payload.name,
        email: payload.email,
        claims: userClaims,
        userRoles: [
          payload.userRole,
          payload.userGroup
        ],
        reason: payload.reason
      }

      if(id){
        user.status = payload.status
      }

      const { success } = await dispatch(
        httpRequestAccount(id ? editAccount(user) : createAccount(user)),
      );

      if (success) navigate(-1);
    }
  };

  const warehousesMap = React.useMemo(
    () =>
      warehouseData?.map(({warehouseCode, name }) => ({
        value: warehouseCode.toString(),
        label: `${warehouseCode} - ${name}`,
      })),
    [warehouseData],
  );

  const handleUserGroupChange = (event) => {
    const {value} = event.target;
    const userGroup = USER_GROUPS[value];

    if(userGroup){
      setSelectedUserGroup(userGroup);
    }
  }

  const handleUserPermissionsChange = (event) => {
    const {value} = event.target;
    
    if(value){
      setSelectedUserPermissions(value)
    }
  }

  const groupHasAllWarehousePermission = (selectedGroup) => {
    return ALL_WAREHOUSE_PERMISSION.includes(selectedGroup)
  }

  const MultipleSelectWarehousesPermission = () => (
    <Permission
      groups={Object.keys(USER_GROUPS).map(group => group)}
      roles={[USER_ROLES.Administrador]}
      component={() => (
        <MultipleSelect
          name="userLinks"
          label={`Depósitos: ${
            groupHasAllWarehousePermission(selectedUserGroup)
              ? 'O grupo selecionado não necessita selecionar os depósitos'
              : '*'
          }`}
          defaultValue={account?.warehouses?.filter(warehouse => warehousesMap.find(({value}) => warehouse === value))}
          options={groupHasAllWarehousePermission(selectedUserGroup)? [] : warehousesMap }
          grid="3/5"
          gridRow="span 3"
          disabled={groupHasAllWarehousePermission(selectedUserGroup)}
        />
      )}
    />
  );

  return (
    <Container>
      <MetaTitle title={`${title} Usuário`} />
      <Title>{title} Usuário</Title>
      <Form
        ref={formRef}
        initialData={accountState.data}
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <Input name="name" label="Nome: *" grid="1/-1" />
        <Input name="email" label="E-mail: *" grid="1/3" disabled={id} />
        <Input
          name="emailConfirmed"
          defaultValue={account?.email}
          label="Confirmar E-mail: *"
          grid="3/-1"
          disabled={id}
        />
        {account?.status !== 'B' && (
          <>
            <Select
              value={selectedUserGroup} 
              label="Grupo: *"
              name="userGroup"
              grid="1/2" 
              options={Object.keys(USER_GROUPS).map(group => {
                return {
                  value: group,
                  label: group
                }
              })}
              onChange={handleUserGroupChange}
            />
            <Select
              value={selectedUserGroup !== USER_GROUPS.DHL? USER_ROLES.Leitor : selectedUserPermissions}
              label="Nível: *"
              name="userRole"
              grid="2/3"
              options={Object.keys(USER_ROLES).map(role => {
                return{
                  value: role,
                  label: role
                }
              })}
              disabled={selectedUserGroup !== USER_GROUPS.DHL}
              onChange={handleUserPermissionsChange}
            />
            {account ? (
              !groupHasAllWarehousePermission(selectedUserGroup) && (
                <MultipleSelectWarehousesPermission />
              )
            ) : (
              <MultipleSelectWarehousesPermission />
            )}
            {id && (
              <Select
                label="Status: "
                name="status"
                grid="span 2"
                options={[
                  {
                    value: 'A',
                    label: 'Ativo',
                  },
                  {
                    value: 'I',
                    label: 'Inativo',
                  },
                  {
                    value: 'B',
                    label: 'Bloqueado',
                  },
                ]}
              />
            )}
            {id && account?.status !== 'B' && (
              <Textarea label="Motivo: " name="reason" grid="span 2" />
            )}
          </>
        )}
        <FormController grid="1/-1">
          <ButtonContent>
            <Permission
              roles={[USER_ROLES.Administrador]}
              component={() => (
                <>
                  {id && account?.status === 'B' ? (
                    <Button
                      type="button"
                      onClick={() => openModal('unlock')}
                      typeName="success"
                    >
                      Desbloquear usuário
                    </Button>
                  ) : (
                    <Button disabled={loadingAccount} type="submit" typeName="success">
                      Enviar
                    </Button>
                  )}
                  {id && account?.status !== 'B' && (
                    <Button
                      type="button"
                      onClick={() => openModal('reset')}
                      typeName="warning"
                    >
                      Resetar senha
                    </Button>
                  )}
                  {id && (
                    <Button
                      type="button"
                      typeName="danger"
                      onClick={() => openModal('delete')}
                    >
                      Deletar
                    </Button>
                  )}
                </>
              )}
            />
            <Link to="/dashboard/users">
              <Button type="button" typeName="welcome">
                Voltar
              </Button>
            </Link>
          </ButtonContent>
        </FormController>
      </Form>
    </Container>
  );
}
